import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import OrgChart from '../components/OrgChart';

export function HomePage({ onNav }) {
  const { data } = useApi('/api/company');
  const co = data?.company;
  return (
    <div className="fade-in">
      <div className="hero">
        <div className="hero-pill">✦ Portal de bienvenida</div>
        <h1>Bienvenido al equipo<br /><span className="accent">brokalia</span></h1>
        <p className="hero-sub">Correduría especializada · Seguros de comunidades</p>
        <p className="hero-desc">{co?.welcome_text || 'La correduría líder en seguros de comunidades de propietarios en España.'}</p>
        <div className="hero-btns">
          <button className="btn btn-primary" onClick={() => onNav('empresa')}>Conoce Brokalia →</button>
          <button className="btn btn-outline" onClick={() => onNav('organigrama')}>Ver organigrama</button>
        </div>
      </div>
      <div className="stats-bar">
        <div className="stat"><span className="stat-num">{co?.stat1_num||'+20'}</span><span className="stat-label">{co?.stat1_label||'Años de historia'}</span></div>
        <div className="stat"><span className="stat-num">{co?.stat2_num||'6'}</span><span className="stat-label">{co?.stat2_label||'Departamentos'}</span></div>
        <div className="stat"><span className="stat-num">{co?.stat3_num||'#1'}</span><span className="stat-label">{co?.stat3_label||'Broker comunidades'}</span></div>
        <div className="stat"><span className="stat-num">{co?.stat4_num||'360°'}</span><span className="stat-label">{co?.stat4_label||'Gestión integral'}</span></div>
      </div>
      <div className="page">
        <div className="card-grid" style={{ marginTop:'1.75rem' }}>
          {[
            { id:'empresa', icon:'📋', title:'La empresa', desc:'Historia, misión, valores y propuesta de valor de Brokalia.' },
            { id:'organigrama', icon:'🗂️', title:'Organigrama', desc:'La estructura completa de la empresa de forma visual.' },
            { id:'departamentos', icon:'🏢', title:'Departamentos', desc:'Conoce cada área, sus funciones y al equipo que la forma.' },
            { id:'directorio', icon:'👥', title:'Directorio', desc:'Equipo de dirección, su cargo y email de contacto.' },
          ].map(c => (
            <div key={c.id} className="card-link" onClick={() => onNav(c.id)}>
              <div className="icon-badge"><span className="icon-glyph">{c.icon}</span></div><h3>{c.title}</h3><p>{c.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function EmpresaPage() {
  const { data, loading } = useApi('/api/company');
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  const { company: co, values, history } = data || {};
  return (
    <div className="page fade-in">
      <div className="section-header"><span className="tag">Sobre nosotros</span><h2>La empresa</h2><div className="rule" /></div>
      <div className="card" style={{ marginBottom:'1.5rem' }}>
        <p style={{ fontSize:14, color:'var(--mid)', lineHeight:1.85, marginBottom:'1rem' }}>{co?.welcome_text}</p>
        <p style={{ fontSize:14, color:'var(--mid)', lineHeight:1.85 }}>{co?.mission_text}</p>
      </div>
      {values?.length > 0 && <>
        <div className="section-header" style={{ marginTop:'1.75rem' }}>
          <span className="tag">Propuesta de valor</span><h2>Lo que nos distingue</h2><div className="rule" />
        </div>
        <div className="card" style={{ marginBottom:'1.5rem' }}>
          {values.map(v => (
            <div key={v.id} className="valor-item">
              <div className="valor-dot"><span /></div>
              <div><strong>{v.title}</strong><p>{v.description}</p></div>
            </div>
          ))}
        </div>
      </>}
      {history?.length > 0 && <>
        <div className="section-header" style={{ marginTop:'1.75rem' }}>
          <span className="tag">Historia</span><h2>Más de 20 años construyendo</h2><div className="rule" />
        </div>
        {history.map(h => (
          <div key={h.id} className="hist-block"><h3>{h.title}</h3><p>{h.content}</p></div>
        ))}
      </>}
    </div>
  );
}

export function OrganigramaPage({ onSelectPerson }) {
  const { data: departments, loading: ld } = useApi('/api/departments');
  const { data: people, loading: lp }      = useApi('/api/people');
  if (ld || lp) return <div className="loading"><div className="spinner" /></div>;
  return (
    <div className="page fade-in">
      <div className="section-header">
        <span className="tag">Estructura</span><h2>Organigrama Brokalia</h2><div className="rule" />
        <p>Haz clic en cualquier persona para ver su ficha y responsabilidades.</p>
      </div>
      <OrgChart people={people||[]} departments={departments||[]} onSelectPerson={onSelectPerson} />
    </div>
  );
}

export function DepartamentosPage({ onSelectPerson }) {
  const { data: departments, loading } = useApi('/api/departments');
  const [open, setOpen] = useState({});
  const toggle = id => setOpen(p => ({ ...p, [id]: !p[id] }));
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  return (
    <div className="page fade-in">
      <div className="section-header">
        <span className="tag">Equipos</span><h2>Departamentos</h2><div className="rule" />
        <p>Explora cada departamento para conocer a tu equipo.</p>
      </div>
      {(departments||[]).map(dept => (
        <div key={dept.id} className="dept-card">
          <div className="dept-header" onClick={() => toggle(dept.id)}>
            <div className="dept-left">
              <div className="dept-icon"><span className="icon-glyph">{dept.icon}</span></div>
              <div>
                <div className="dept-name">{dept.name}</div>
                {dept.people?.find(p => p.is_director) && (
                  <div className="dept-director">{dept.people.find(p=>p.is_director).name} · {dept.people.find(p=>p.is_director).role}</div>
                )}
              </div>
            </div>
            <div className={`dept-arrow${open[dept.id]?' open':''}`}>›</div>
          </div>
          <div className={`dept-body${open[dept.id]?' open':''}`}>
            {dept.description && <p className="dept-desc">{dept.description}</p>}
            {dept.people?.length > 0 && (
              <div className="team-grid">
                {dept.people.map(p => (
                  <div key={p.id} className="team-member" onClick={() => onSelectPerson(p)}>
                    <div className="member-avatar">{p.photo_url?<img src={p.photo_url} alt={p.name}/>:p.initials}</div>
                    <div className="member-name">{p.name}</div>
                    <div className="member-role">{p.role}</div>
                  </div>
                ))}
              </div>
            )}
            {dept.subdepartments?.map(sub => (
              <div key={sub.id}>
                <div className="sub-dept-title">{sub.name}</div>
                <p className="sub-dept-desc">{sub.description}</p>
              </div>
            ))}
            {dept.tags?.length > 0 && (
              <div className="tags" style={{ marginTop:12 }}>
                {dept.tags.map((t,i) => <span key={i} className="tag-chip">{t}</span>)}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function DirectorioPage({ onSelectPerson }) {
  const { data: people, loading } = useApi('/api/people');
  const [query, setQuery] = useState('');
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  const filtered = (people||[]).filter(p => p.is_director === 1).filter(p => {
    const q = query.toLowerCase();
    return !q || p.name?.toLowerCase().includes(q) || p.role?.toLowerCase().includes(q) || p.dept_name?.toLowerCase().includes(q);
  });
  return (
    <div className="page fade-in">
      <div className="section-header"><span className="tag">Equipo</span><h2>Directorio de dirección</h2><div className="rule" /></div>
      <input className="search-input" placeholder="Buscar por nombre, cargo o departamento..." value={query} onChange={e => setQuery(e.target.value)} />
      <div className="contact-list">
        {filtered.length===0 && <div className="empty">Sin resultados para "{query}"</div>}
        {filtered.map(p => (
          <div key={p.id} className="contact-item" onClick={() => onSelectPerson(p)}>
            <div className="contact-av">{p.photo_url?<img src={p.photo_url} alt={p.name}/>:p.initials}</div>
            <div style={{ flex:1 }}>
              <div className="contact-name">{p.name}</div>
              <div className="contact-dept">{p.role}{p.dept_name?` · ${p.dept_name}`:''}</div>
              {p.email && <div className="contact-email">{p.email}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
